import {
  Component,
  computed,
  ElementRef,
  HostAttributeToken,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import type { AbstractControl } from '@angular/forms';
import { KeyValuePipe } from '@angular/common';
import { MAT_ERROR } from '@angular/material/form-field';
import { _IdGenerator } from '@angular/cdk/a11y';
import { ResolveErrorKeyPipe } from './resolve-error-key.pipe';
import { fadeIn2XFastEnter, fadeOut2XFastLeave } from '@pct-ng/animation';
import { IsSerializablePipe } from '@pct-ng/common/pipe';
import type { PctFormGroup } from '../reactive-forms/pct-form-group';
import { I18nProviderInjectionToken } from '../i18n/scope';
import { searchRootFormGroup } from '../reactive-forms/search-root-form-group';
import { provideI18nScope } from '@pct-ng/transloco';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'mat-error',
  imports: [/*MatError,*/ KeyValuePipe, ResolveErrorKeyPipe, IsSerializablePipe, TranslocoPipe],
  templateUrl: './control-error.component.html',
  providers: [
    provideI18nScope(I18nProviderInjectionToken),
    { provide: MAT_ERROR, useValue: ControlErrorComponent },
  ],
  host: {
    class: 'mat-mdc-form-field-error mat-mdc-form-field-bottom-align',
    'aria-atomic': 'true',
    '[id]': 'id',
  },
  animations: [fadeIn2XFastEnter, fadeOut2XFastLeave],
})
export class ControlErrorComponent {
  protected readonly i18nScope = inject<string>(I18nProviderInjectionToken);

  // Copy from mat-error
  readonly id = signal(inject(_IdGenerator).getId('mat-mdc-error-'));
  // Custom codes
  readonly parentForm = input<PctFormGroup | undefined>();
  protected readonly rootFormGroup = computed(() => {
    const parentForm = this.parentForm();
    if (parentForm !== undefined) {
      return searchRootFormGroup(parentForm);
    }
    return undefined;
  });
  readonly control = input.required<AbstractControl>();
  readonly overrideErrors = input<Record<string, string> | undefined>(undefined);

  // Copy from mat-error
  constructor() {
    const ariaLive = inject(new HostAttributeToken('aria-live'), { optional: true });

    // If no aria-live value is set add 'polite' as a default. This is preferred over setting
    // role='alert' so that screen readers do not interrupt the current task to read this aloud.
    if (!ariaLive) {
      const elementRef = inject(ElementRef);
      elementRef.nativeElement.setAttribute('aria-live', 'polite');
    }
  }
}
